import {
    getApiError,
    getTaskListsThunk,
    getWorkGetScheduleThunk,
    getWorkStartAndGetScheduleThunk,
} from "tm-shared/api";
import { UserRole } from "tm-shared/types";

import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import useAsyncAction from "@/hooks/useAsyncAction";
import { useAsyncTaskQueue } from "@/hooks/useAsyncTaskQueue";
import { useInitRetryRequests } from "@/pages/WorkPlan/components/WorkControls/useInitRetryRequests";
import {
    getKpiGetMarketProgressesThunk,
    getPlansByMarketIdThunk,
    setIsInitialDataLoading,
} from "@/store/slices/offline";
import { setSVActiveSchedule } from "@/store/slices/sv";
import { resetUiState, setInitFailedRequests } from "@/store/slices/ui";

import { sendUserLocation } from "../utils/locationUtils";

export const useLoadUserSchedule = () => {
    const dispatch = useAppDispatch();
    const { offlineMode } = useAppSelector((state) => state.offline);
    const { initFailedRequests } = useAppSelector((s) => s.ui);

    const { queue, progress: queueProgress } = useAsyncTaskQueue();
    const initRequests = useInitRetryRequests();

    const [handleLoadUserSchedule, isProcessing] = useAsyncAction(
        async (userId: string, roleSlug: string, routeId?: string) => {
            queue.clear();

            let scheduleLoaded = false;

            queue.add("Завантаження розкладу роботи", async () => {
                try {
                    const { result } = await dispatch(
                        getWorkStartAndGetScheduleThunk({ userId, roleSlug }),
                    ).unwrap();

                    scheduleLoaded = true;

                    const marketIds = Object.keys(result?.markets ?? {});

                    if (marketIds.length) {
                        await loadAdditionalData(marketIds, roleSlug, routeId);
                    }
                } catch (error) {
                    const { code } = getApiError(error);

                    if (code === "-32400") {
                        await handleScheduleError(userId, roleSlug, routeId);
                        scheduleLoaded = true;
                        return;
                    }

                    throw error;
                }
            });

            initFailedRequests.forEach((type) => {
                const cmd = initRequests[type];
                if (!cmd) return;
                queue.add(cmd.title, cmd.run);
            });

            try {
                await queue.runParallel();
            } finally {
                if (scheduleLoaded) {
                    dispatch(resetUiState());
                    dispatch(setSVActiveSchedule(userId));
                }

                dispatch(setIsInitialDataLoading(false));
                dispatch(setInitFailedRequests([]));

                if (!offlineMode) {
                    sendUserLocation();
                }
            }
        },
    );

    const loadAdditionalData = async (
        marketIds: string[],
        roleSlug: string,
        routeId?: string,
    ) => {
        await queue.add("Завантаження інформації про задачі", () =>
            dispatch(
                getTaskListsThunk({
                    targetIds: marketIds,
                    roleSlug,
                }),
            ).unwrap(),
        );

        if (roleSlug === UserRole.Ta && routeId) {
            await queue.add(
                "Завантаження інформації про плани на ТТ",
                async () => {
                    await dispatch(
                        getPlansByMarketIdThunk({
                            marketIds,
                            routeId,
                        }),
                    );
                    queue.add("Завантаження інформації про факти на ТТ", () =>
                        dispatch(
                            getKpiGetMarketProgressesThunk({
                                marketIds,
                            }),
                        ),
                    );
                },
            );
        }
    };

    const handleScheduleError = async (
        userId: string,
        roleSlug: string,
        routeId?: string,
    ) => {
        const { result } = await dispatch(
            getWorkGetScheduleThunk({ userId, roleSlug }),
        ).unwrap();

        const marketIds = Object.keys(result?.markets ?? {});

        if (marketIds.length) {
            await loadAdditionalData(marketIds, roleSlug, routeId);
        }
    };

    return {
        handleLoadUserSchedule,
        isProcessing,
        queueProgress,
    };
};
